import { QueryHookOptions, gql, useQuery } from '@apollo/client'
import { NAVIGATION_LINK_FRAGMENT } from './navigationQueries'
import BRAND_FRAGMENT from './BRAND_FRAGMENT'
import PROJECT_FRAGMENT from './PROJECT_FRAGMENT'
import { Brand, Project } from 'types'
import { useEntitiesWithContent } from 'mammoth/hooks/useContent'
import { usePointsWithContent } from './employeePoints'

export const POINT_ENTITY_FRAGMENT = gql`
    fragment PointFields on PointEntity {
        id
        attributes {
            Engagement_Type
            Points
            Emoji_Background_Color
            Transaction_Type
        }
    }
`

export const POINTS_CARD_FRAGMENT = gql`
    fragment PointsCard on ComponentMoleculesPointsCard {
        Before_Points_Text
        Points_Text
        After_Points_Text
        CTA {
            ...NavigationLink
        }
    }
    ${NAVIGATION_LINK_FRAGMENT}
`

export const POINT_REDEMPTIONS_PROJECTS_QUERY = gql`
    query PointRedemptionsProjects($filters: ProjectFiltersInput, $pagination: PaginationArg, $sort: [String]) {
        pointRedemptionsPage(publicationState: LIVE) {
            data {
                attributes {
                    Carbon_Removals_Tab {
                        Projects(filters: $filters, pagination: $pagination, sort: $sort) {
                            data {
                                id
                                attributes {
                                    ...ProjectFields
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    ${PROJECT_FRAGMENT}
`

export const usePointRedemptionsProjects = (options?: QueryHookOptions) => {
    const { data, loading, error, ...rest } = useQuery(POINT_REDEMPTIONS_PROJECTS_QUERY, options)
    const {
        data: projectsData,
        loading: contentLoading,
        error: contentError
    } = useEntitiesWithContent(
        'projects',
        data?.pointRedemptionsPage?.data?.attributes?.Carbon_Removals_Tab?.Projects?.data
    )
    return {
        data: projectsData as Project[],
        loading: loading || contentLoading,
        error: error || contentError,
        ...rest
    }
}

export const POINT_REDEMPTIONS_BRANDS_QUERY = gql`
    query PointRedemptionsBrands($filters: BrandFiltersInput, $pagination: PaginationArg, $sort: [String]) {
        pointRedemptionsPage(publicationState: LIVE) {
            data {
                attributes {
                    Gift_Cards_Tab {
                        Brands(filters: $filters, pagination: $pagination, sort: $sort) {
                            data {
                                ...BrandFields
                            }
                        }
                    }
                }
            }
        }
    }
    ${BRAND_FRAGMENT}
`

export const usePointRedemptionsBrands = (options?: QueryHookOptions) => {
    const { data, loading, error, ...rest } = useQuery(POINT_REDEMPTIONS_BRANDS_QUERY, options)
    const {
        data: brandsData,
        loading: contentLoading,
        error: contentError
    } = useEntitiesWithContent('brands', data?.pointRedemptionsPage?.data?.attributes?.Gift_Cards_Tab?.Brands?.data)
    return {
        data: brandsData as Brand[],
        loading: loading || contentLoading,
        error: error || contentError,
        ...rest
    }
}

const POINT_REDEMPTIONS_REWARDS_QUERY = gql`
    query PointRedemptionsPage {
        pointRedemptionsPage(publicationState: LIVE) {
            data {
                attributes {
                    Rewards {
                        data {
                            ...PointFields
                        }
                    }
                }
            }
        }
    }
    ${POINT_ENTITY_FRAGMENT}
`

export const usePointRedemptionsRewards = () => {
    const { data, loading, error, ...rest } = useQuery(POINT_REDEMPTIONS_REWARDS_QUERY)
    const {
        data: rewardsData,
        loading: contentLoading,
        error: contentError
    } = usePointsWithContent(data?.pointRedemptionsPage?.data?.attributes?.Rewards?.data)

    return {
        data: rewardsData,
        loading: loading || contentLoading,
        error: error || contentError,
        ...rest
    }
}

export const REDEEM_POINTS_MUTATION = gql`
    mutation RedeemPoints(
        $rewardValue: Int!
        $rewardType: RewardType!
        $brandId: ID
        $projectId: ID
        $projectName: String
    ) {
        redeemPoints(
            rewardValue: $rewardValue
            rewardType: $rewardType
            brandId: $brandId
            projectId: $projectId
            projectName: $projectName
        ) {
            code
        }
    }
`
